/**
 * Marks an element (typically a grip handle) as draggable. Returns the ref to
 * register, the ARIA/`tabIndex` attributes to spread, and the pointer/keyboard
 * listeners. The element stays the consumer's — this hook adds behaviour, not
 * markup.
 *
 * `roleDescription` is consumer-localized (the package ships no strings); a
 * neutral English default is used only if omitted. Ids are assumed stable for
 * the lifetime of the element.
 */
import {
  useRef,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useDndContext } from './DndContext.tsx'
import type { DndData } from './types.ts'

export interface UseDraggableArgs {
  id: string
  data?: DndData
  disabled?: boolean
  /** Localized; announced by screen readers as the element's role. */
  roleDescription?: string
}

export interface DraggableAttributes {
  role: 'button'
  tabIndex: number
  'aria-disabled': boolean | undefined
  'aria-roledescription': string
  'aria-describedby': string
  'aria-pressed': boolean
  'data-dragging': '' | undefined
  style: CSSProperties
}

export interface UseDraggableResult {
  /**
   * Ref for the *measured* element — the one that sizes the drag overlay and
   * resolves the scroll container. Put this on the whole card when you want the
   * overlay to mirror it, even if dragging starts from a smaller handle.
   */
  setNodeRef: (node: HTMLElement | null) => void
  /**
   * Optional ref for the activator (the grip handle) — where focus returns after
   * a keyboard drop. Omit it (drag by the body) and focus falls back to the
   * measured node. Spread `attributes`/`listeners` on this same element.
   */
  setActivatorNodeRef: (node: HTMLElement | null) => void
  attributes: DraggableAttributes
  listeners: {
    onPointerDown: (e: ReactPointerEvent) => void
    onKeyDown: (e: ReactKeyboardEvent) => void
  }
  isDragging: boolean
}

export function useDraggable({
  id,
  data,
  disabled = false,
  roleDescription,
}: UseDraggableArgs): UseDraggableResult {
  const ctx = useDndContext()

  // Live getters so data/disabled changes don't re-register the entry.
  const dataRef = useRef(data)
  dataRef.current = data
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled

  const entryRef = useRef({
    id,
    node: null as HTMLElement | null,
    activatorNode: null as HTMLElement | null,
    getData: () => dataRef.current ?? {},
    isDisabled: () => disabledRef.current,
  })
  entryRef.current.id = id

  // Register/unregister in the ref callback (commit phase) rather than an effect,
  // so the entry is in the registry synchronously the instant its node mounts.
  // The keyboard-drop focus restore (`focus()` on the re-parented card next
  // frame) would otherwise race React's passive-effect flush. The callback is
  // stable, so React runs it only on attach (node) / detach (null), not per
  // render; the `unregisterRef` guard keeps it idempotent under StrictMode's
  // double-invoke.
  const { registerDraggable } = ctx
  const unregisterRef = useRef<(() => void) | null>(null)
  const setNodeRef = useRef((node: HTMLElement | null) => {
    entryRef.current.node = node
    if (node) {
      if (!unregisterRef.current) unregisterRef.current = registerDraggable(entryRef.current)
    } else {
      unregisterRef.current?.()
      unregisterRef.current = null
    }
  }).current

  // The activator lives on the same registered entry, so updating the field is
  // visible to the context without re-registering. Stable callback → runs only
  // on attach/detach of the handle element.
  const setActivatorNodeRef = useRef((node: HTMLElement | null) => {
    entryRef.current.activatorNode = node
  }).current

  const isDragging = ctx.activeId === id

  return {
    setNodeRef,
    setActivatorNodeRef,
    attributes: {
      role: 'button',
      tabIndex: disabled ? -1 : 0,
      'aria-disabled': disabled || undefined,
      'aria-roledescription': roleDescription ?? 'Draggable item',
      'aria-describedby': ctx.instructionsId,
      'aria-pressed': isDragging,
      'data-dragging': isDragging ? '' : undefined,
      // Stop the browser claiming the gesture for scroll on touch. Consumers can
      // still override via className/style if they spread after this.
      style: { touchAction: 'none' },
    },
    listeners: {
      onPointerDown: (e) => ctx.startPointerDrag(id, e),
      onKeyDown: (e) => ctx.handleDraggableKeyDown(id, e),
    },
    isDragging,
  }
}
